import {
  Button,
  Modal,
  SidebarTabPanel,
} from "@risalabs_frontend_org/oasis-ui-kit";
import moment from "moment";
import {
  FaxLetterCategory,
  FaxLetterDocument,
} from "../../../api/postCall/faxLetter";
import { useCandidDocumentDownloadUrl } from "../../../components/modals/candidRpaStatus/hooks/useCandidDocumentDownloadUrl";
import PdfRender from "../../../components/pdfRender/pdfRender";
import { DocumentTypesForOrder } from "../../../enums/documentTypes";
import { ModalId } from "../../../enums/modalId";
import DownloadIcon from "../../../svg/download-icon";
import {
  DOCUMENTS_LABEL,
  DOWNLOAD_LABEL,
} from "../../authorisationDetails/utils/constants";

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  [DocumentTypesForOrder.FILLED_FORM]: "Filled Form",
  [DocumentTypesForOrder.APPROVAL_LETTER]: "Approval Letter",
  [DocumentTypesForOrder.DENIAL_LETTER]: "Denial Letter",
  [DocumentTypesForOrder.NAR_LETTER]: "NAR Letter",
  [DocumentTypesForOrder.AUTH_LETTER]: "Auth Letter",
  [DocumentTypesForOrder.FAX_LETTER]: "Fax Letter",
};

const FAX_CATEGORY_LABELS: Record<FaxLetterCategory, string> = {
  [FaxLetterCategory.Approval]: "Approval Letter",
  [FaxLetterCategory.Denial]: "Denial Letter",
  [FaxLetterCategory.NoAuthRequired]: "NAR Letter",
  [FaxLetterCategory.AdditionalInfoP2P]: "Additional Info / P2P Letter",
  [FaxLetterCategory.UnableToProcess]: "Unable to Process Letter",
  [FaxLetterCategory.Renewal]: "Renewal Letter",
  [FaxLetterCategory.Others]: "Other Letter",
  [FaxLetterCategory.Unclassified]: "Unclassified Letter",
};

/**
 * Sidebar label for a document: its type, else its title.
 *
 * @param document - A document on this drug
 * @returns The label shown in the document list
 */
export const documentLabel = (document: FaxLetterDocument): string =>
  (document.doc_type === DocumentTypesForOrder.FAX_LETTER && document.category
    ? FAX_CATEGORY_LABELS[document.category]
    : undefined) ??
  DOCUMENT_TYPE_LABELS[document.doc_type ?? ""] ??
  document.doc_title ??
  "Document";

/**
 * Upload time as shown in the preview header, e.g. "Jul 29, 2026 7:32 AM (IST)".
 *
 * @param value - ISO-8601 timestamp
 * @returns The formatted local time, or "" when absent or unparseable
 */
export const formatUploadTime = (value?: string | null): string => {
  const parsed = moment(value ?? "", moment.ISO_8601, true);
  if (!parsed.isValid()) return "";
  const zone = new Intl.DateTimeFormat("en-US", { timeZoneName: "short" })
    .formatToParts(parsed.toDate())
    .find((part) => part.type === "timeZoneName")?.value;
  return `${parsed.format("MMM D, YYYY h:mm A")}${zone ? ` (${zone})` : ""}`;
};

const DocumentPreview = ({
  document,
  onSwitchDocument,
}: {
  document: FaxLetterDocument;
  onSwitchDocument?: () => void;
}) => {
  const { url, isLoading, error } = useCandidDocumentDownloadUrl(
    document.doc_path ?? "",
  );
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-primaryGray-14 px-4 py-2 text-tiny font-semibold text-primaryGray-1">
        <div className="flex min-w-0 max-w-full items-center gap-1.5">
          <span className="truncate" title={document.doc_title ?? ""}>
            {document.doc_title || documentLabel(document)}
          </span>
          <button
            type="button"
            aria-label={DOWNLOAD_LABEL}
            disabled={!url}
            onClick={() => window.open(url, "_blank")}
            className="shrink-0 rounded focus:outline-none focus:ring-2 focus:ring-primaryGray-15 disabled:cursor-not-allowed"
          >
            <DownloadIcon width={16} height={16} color="#0056D6" />
          </button>
        </div>
        <span>{formatUploadTime(document.doc_upload_date)}</span>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-primaryGray-10">
            Loading document...
          </div>
        ) : error || !url ? (
          <div className="flex h-full items-center justify-center text-sm text-red-600">
            {error || "No document available"}
          </div>
        ) : (
          <PdfRender file={url} />
        )}
      </div>
      {onSwitchDocument && (
        <footer className="flex shrink-0 justify-end border-t border-primaryGray-14 bg-white px-4 py-3">
          <Button
            buttonType="primary"
            size="medium"
            disabled={false}
            onClick={onSwitchDocument}
          >
            Switch Document
          </Button>
        </footer>
      )}
    </div>
  );
};

interface DocumentsModalProps {
  documents: FaxLetterDocument[];
  currentDocumentId: string;
  onSwitchDocument: (documentId: string) => void;
  onClose: () => void;
}

/** Preview the drug's documents and switch to an attached letter's independent human review. */
const DocumentsModal = ({
  documents,
  currentDocumentId,
  onSwitchDocument,
  onClose,
}: DocumentsModalProps) => {
  const tabs = documents.map((document, index) => ({
    id: document.id || `document-${index}`,
    label: documentLabel(document),
  }));
  return (
    <Modal
      dialogId={ModalId.FAX_LETTER_DOCUMENTS_MODAL}
      title={DOCUMENTS_LABEL}
      saveButtonText=""
      cancelText=""
      onSave={onClose}
      onClose={onClose}
      hideFooter
    >
      <div className="h-[542px] max-h-[calc(100vh-7rem)] w-full py-4">
        <SidebarTabPanel
          tabs={tabs}
          defaultValue={tabs[0]?.id}
          sidebarWidth="clamp(100px, 24vw, 180px)"
          activeTextColor="#0F0F0F"
          activeBorderColor="#2E2E2E"
          className="h-full"
        >
          {(activeTabId) => {
            const index = tabs.findIndex((tab) => tab.id === activeTabId);
            const document = documents[index];
            const reviewDocumentId = document?.review_document_id;
            return document ? (
              <DocumentPreview
                key={activeTabId}
                document={document}
                onSwitchDocument={
                  reviewDocumentId && reviewDocumentId !== currentDocumentId
                    ? () => onSwitchDocument(reviewDocumentId)
                    : undefined
                }
              />
            ) : null;
          }}
        </SidebarTabPanel>
      </div>
    </Modal>
  );
};

export default DocumentsModal;
